/**
 * RSIPanel — Wilder's RSI (length from indicatorConfigStore, default 14) plus
 * its moving average, in its own chart below the main chart, the
 * first of the single-slot sub-panel indicators (indicatorStore.activeSubPanel).
 *
 * Same chart-instance pattern as DeltaPanel (two-way synced, see subPanelSync), but no WebSocket:
 * like EMAOverlay, the RSI is computed client-side from marketStore's candles
 * (utils/klineAnalytics.computeRSI), so it recomputes whenever they change and
 * follows replay's cursor for free.
 */

import { useEffect, useRef, useState } from 'react';
import {
  createChart, LineStyle,
  type IChartApi, type IPriceLine, type ISeriesApi, type LineWidth,
} from 'lightweight-charts';
import { useMarketStore } from '../../store/marketStore';
import { useReplayStore } from '../../store/replayStore';
import { useIndicatorConfigStore } from '../../store/indicatorConfigStore';
import { useThemeStore, type Theme } from '../../store/themeStore';
import { toChartTime } from '../../utils/chartTime';
import { computeRSI, computeRSIMA, type RSIPoint } from '../../utils/klineAnalytics';
import type { Candle } from '../../types/market';
import { subChartThemeOptions } from './DeltaPanel';
import { linkSubPanel, SUB_PANEL_INTERACTION } from './subPanelSync';
import { SubPanelDrawingPane } from './SubPanelDrawingPane';

// Fills are faint enough to read on both themes, so they stay constant.
const FILL_OB   = 'rgba(38,166,154,0.25)';   // RSI beyond 70 / 30
const BAND_FILL = 'rgba(126,87,194,0.06)';   // 30-70 band
const CLEAR     = 'rgba(0,0,0,0)';

// Pin the scale to the oscillator's full 0-100 range instead of autoscaling
// to whatever is on screen. Shared by every series on the panel so none of
// them can stretch the scale and knock the others out of line.
const rsi01 = () => ({ priceRange: { minValue: 0, maxValue: 100 } });

// Baseline defaults are loud (3px line, axis label, crosshair dot) — the
// fill-only series turn all of that off.
const FILL_ONLY = {
  lineVisible: false,
  priceLineVisible: false,
  lastValueVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: rsi01,
} as const;

// Matches TradingChart's rightPriceScale.minimumWidth so both plot areas start
// at the same width; see matchAxisWidth for when the main axis grows past it.
export const AXIS_MIN_WIDTH = 72;

export function refLineColors(theme: Theme) {
  return theme === 'dark'
    ? { band: '#6e7681', mid: '#3d444d' }
    : { band: '#9ca3af', mid: '#d1d5db' };
}

// One entry per candle: whitespace (time-only) for the warm-up bars, then the
// points. Keeps every series index-aligned with the main chart, which the
// logical-range sync needs. `points` must end on the last candle.
function padded(visible: Candle[], points: RSIPoint[]) {
  const warmUp = visible.length - points.length;
  return [
    ...visible.slice(0, warmUp).map((c) => ({ time: toChartTime(c.t) })),
    ...points.map((p) => ({ time: toChartTime(p.time), value: p.value })),
  ];
}

interface RSIPanelProps {
  /** Ref to the main candlestick chart — this panel follows its time scale. */
  sharedChartRef: React.RefObject<IChartApi | null>;
}

export function RSIPanel({ sharedChartRef }: RSIPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);
  const seriesRef    = useRef<ISeriesApi<'Line'> | null>(null);
  const maRef        = useRef<ISeriesApi<'Line'> | null>(null);
  const bandRef      = useRef<ISeriesApi<'Baseline'> | null>(null);  // 30-70 fill
  const obFillRef    = useRef<ISeriesApi<'Baseline'> | null>(null);  // > 70 fill
  const osFillRef    = useRef<ISeriesApi<'Baseline'> | null>(null);  // < 30 fill
  const bandLinesRef = useRef<IPriceLine[]>([]);   // 70 / 30
  const midLineRef   = useRef<IPriceLine | null>(null);  // 50

  // Gates the drawing pane and auto-scale button, which need the chart to exist on mount.
  const [chartReady, setChartReady] = useState(false);
  const [current, setCurrent]     = useState<number | null>(null);
  const [currentMA, setCurrentMA] = useState<number | null>(null);

  const candles          = useMarketStore((s) => s.candles);
  const theme            = useThemeStore((s) => s.theme);
  const replayActive     = useReplayStore((s) => s.isActive);
  const replayCursorTime = useReplayStore((s) => s.cursorTime);
  const cfg              = useIndicatorConfigStore((s) => s.configs.rsi);

  // Keep this panel's price axis exactly as wide as the main chart's, so the
  // two plot areas — and therefore their bars — line up vertically. The
  // shared minimumWidth covers the usual case; this catches a main axis whose
  // labels (e.g. a 6-digit price) push it wider than that.
  const matchAxisWidth = () => {
    const main  = sharedChartRef.current;
    const chart = chartRef.current;
    if (!main || !chart) return;
    const w = main.priceScale('right').width();
    chart.priceScale('right').applyOptions({ minimumWidth: Math.max(w, AXIS_MIN_WIDTH) });
  };

  // ── Chart initialisation + time-scale sync ────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const themeOpts = subChartThemeOptions(theme);
    const chart = createChart(containerRef.current, {
      layout: themeOpts.layout,
      grid: themeOpts.grid,
      crosshair: themeOpts.crosshair,
      leftPriceScale: {
        visible: false,
      },
      rightPriceScale: {
        borderColor: themeOpts.rightPriceScale.borderColor,
        scaleMargins: { top: 0.05, bottom: 0.05 },
        minimumWidth: AXIS_MIN_WIDTH,
      },
      timeScale: {
        borderColor: themeOpts.timeScale.borderColor,
        timeVisible: true,
        secondsVisible: false,
      },
      // Scrolls/zooms like the main chart; subPanelSync links the two ranges.
      ...SUB_PANEL_INTERACTION,
      width:  containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    });

    // Series draw in creation order, so fills go first and the RSI line last.
    // The band is a constant 70 over a 30 baseline: a filled 30-70 strip.
    const band = chart.addBaselineSeries({
      ...FILL_ONLY,
      baseValue: { type: 'price', price: 30 },
      topFillColor1: BAND_FILL, topFillColor2: BAND_FILL,
      bottomFillColor1: CLEAR,  bottomFillColor2: CLEAR,
    });
    // RSI itself against a 70 / 30 baseline, filling only the side beyond it.
    const obFill = chart.addBaselineSeries({
      ...FILL_ONLY,
      baseValue: { type: 'price', price: 70 },
      topFillColor1: FILL_OB,  topFillColor2: FILL_OB,
      bottomFillColor1: CLEAR, bottomFillColor2: CLEAR,
    });
    const osFill = chart.addBaselineSeries({
      ...FILL_ONLY,
      baseValue: { type: 'price', price: 30 },
      topFillColor1: CLEAR,      topFillColor2: CLEAR,
      bottomFillColor1: FILL_OB, bottomFillColor2: FILL_OB,
    });
    // Read the config imperatively: edits restyle/recompute the existing
    // series in the effects below rather than recreating the chart.
    const [rsiLine, maLine] = useIndicatorConfigStore.getState().configs.rsi.lines;
    const ma = chart.addLineSeries({
      color: maLine.color,
      lineWidth: maLine.width as LineWidth,
      visible: maLine.visible,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
      autoscaleInfoProvider: rsi01,
    });

    const series = chart.addLineSeries({
      color: rsiLine.color,
      lineWidth: rsiLine.width as LineWidth,
      visible: rsiLine.visible,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: false,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
      autoscaleInfoProvider: rsi01,
    });

    // Price lines survive setData, so the reference levels are created once.
    // They hang off the band (never hidden) rather than the RSI line, so
    // hiding the RSI line in settings doesn't take the 70/50/30 levels with it.
    const colors = refLineColors(theme);
    const refLine = (price: number, color: string, lineStyle: LineStyle) =>
      band.createPriceLine({
        price, color, lineWidth: 1, lineStyle, axisLabelVisible: false, title: '',
      });
    bandLinesRef.current = [
      refLine(70, colors.band, LineStyle.Dashed),
      refLine(30, colors.band, LineStyle.Dashed),
    ];
    midLineRef.current = refLine(50, colors.mid, LineStyle.Dotted);

    chartRef.current  = chart;
    seriesRef.current = series;
    maRef.current     = ma;
    bandRef.current   = band;
    obFillRef.current = obFill;
    osFillRef.current = osFill;

    // Two-way sync (subPanelSync guards the feedback loop that keeps
    // DeltaPanel one-way). Unlike DeltaPanel this syncs by *logical* (bar
    // index) range: the RSI is computed from the very same candles the main
    // chart holds, and the data effect pads the warm-up with whitespace, so
    // index N is the same bar on both charts. That also carries over the
    // main chart's right-edge offset, which a time range can't express.
    const unlink = linkSubPanel(sharedChartRef, chart, containerRef.current, matchAxisWidth);
    setChartReady(true);

    const observer = new ResizeObserver(() => {
      if (!containerRef.current) return;
      chart.applyOptions({
        width:  containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      unlink();
      setChartReady(false);
      chart.remove();  // disposes every series on this chart
      chartRef.current  = null;
      seriesRef.current = null;
      maRef.current     = null;
      bandRef.current   = null;
      obFillRef.current = null;
      osFillRef.current = null;
      bandLinesRef.current = [];
      midLineRef.current   = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedChartRef]);

  // Re-skin the native chart (grid/axis/crosshair + reference-line colors) on
  // theme change without recreating it, so data and range survive the switch.
  useEffect(() => {
    if (!chartRef.current) return;
    const themeOpts = subChartThemeOptions(theme);
    chartRef.current.applyOptions({
      layout: themeOpts.layout,
      grid: themeOpts.grid,
      crosshair: themeOpts.crosshair,
      leftPriceScale: { borderColor: themeOpts.leftPriceScale.borderColor },
      rightPriceScale: { borderColor: themeOpts.rightPriceScale.borderColor },
      timeScale: { borderColor: themeOpts.timeScale.borderColor },
    });
    const colors = refLineColors(theme);
    bandLinesRef.current.forEach((l) => l.applyOptions({ color: colors.band }));
    midLineRef.current?.applyOptions({ color: colors.mid });
  }, [theme]);

  // ── Recompute on every candles change (incl. the forming bar's ticks) ─────
  // and whenever the RSI / MA lengths are edited.
  useEffect(() => {
    const series = seriesRef.current;
    const ma     = maRef.current;
    const band   = bandRef.current;
    const obFill = obFillRef.current;
    const osFill = osFillRef.current;
    if (!series || !ma || !band || !obFill || !osFill) return;

    // In replay, marketStore still holds the full window — slice at the cursor
    // exactly as ReplayEngine does for the main chart, so bar counts match.
    const visible = replayActive && replayCursorTime != null
      ? candles.filter((c) => c.t <= replayCursorTime)
      : candles;

    const points   = computeRSI(visible, cfg.period);
    const maPoints = computeRSIMA(visible, cfg.period, cfg.maPeriod);
    // Every series gets exactly visible.length entries (see padded).
    const rsiData = padded(visible, points);

    // The band has no warm-up — it spans the whole history.
    band.setData(visible.map((c) => ({ time: toChartTime(c.t), value: 70 })));
    obFill.setData(rsiData);
    osFill.setData(rsiData);
    ma.setData(padded(visible, maPoints));
    series.setData(rsiData);

    // setData can refit this chart's own range — pull it back to the main one.
    const mainRange = sharedChartRef.current?.timeScale().getVisibleLogicalRange();
    if (mainRange) chartRef.current?.timeScale().setVisibleLogicalRange(mainRange);
    matchAxisWidth();

    setCurrent(points.at(-1)?.value ?? null);
    setCurrentMA(maPoints.at(-1)?.value ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, replayActive, replayCursorTime, sharedChartRef, cfg.period, cfg.maPeriod]);

  // ── Re-style on color/width/visibility edits (no recompute) ───────────────
  useEffect(() => {
    const [rsiLine, maLine] = cfg.lines;
    seriesRef.current?.applyOptions({ color: rsiLine.color, lineWidth: rsiLine.width as LineWidth, visible: rsiLine.visible });
    maRef.current?.applyOptions({ color: maLine.color, lineWidth: maLine.width as LineWidth, visible: maLine.visible });
  }, [cfg.lines]);

  const [rsiLine, maLine] = cfg.lines;

  return (
    <div className="relative w-full h-full" data-drawing-pane="rsi">
      {/* Stat overlay */}
      <div className="absolute top-1 left-3 z-10 flex items-center gap-1.5 pointer-events-none select-none text-xs font-mono">
        <span className="text-[var(--text-muted)] font-sans">RSI {cfg.period}</span>
        {rsiLine.visible && current !== null && (
          <span style={{ color: rsiLine.color }}>{current.toFixed(2)}</span>
        )}
        {maLine.visible && (
          <>
            <span className="text-[var(--text-muted)] font-sans">· MA {cfg.maPeriod}</span>
            {currentMA !== null && (
              <span style={{ color: maLine.color }}>{currentMA.toFixed(2)}</span>
            )}
          </>
        )}
      </div>

      <div ref={containerRef} className="w-full h-full" />

      {/* Drawings + "A" (auto-scale) button + style toolbar. */}
      {chartReady && (
        <SubPanelDrawingPane paneId="rsi" kind="rsi" chartRef={chartRef} seriesRef={bandRef} sharedChartRef={sharedChartRef} />
      )}
    </div>
  );
}
